import {StateReducerMode} from '@stefanholzapfel/lit-state';
import {FilterParams} from '../../../../backend/src/api/queries/reports.query';

export interface NetworkedList<T> {
    data: T[];
    filterParams?: FilterParams;
    count?: number;
    page?: number;
}

export interface NetworkingState {
    executing: boolean;
    error: string;
    abortController: AbortController;
    abortControllers: AbortController[];
    response?: any;
}
export type NetworkingAction = '_fetch' | '_add';
export type NetworkingCapability<K extends NetworkingAction> = { [key in K] : NetworkingState };
export type NetworkingCapabilities<K extends NonEmptyArray<NetworkingAction>> = { [key in ValuesOf<K>]: NetworkingState };

export type ValuesOf<T extends NetworkingAction[] | any[]> = T[number];
type NonEmptyArray<T> = [T, ...T[]];

// Factories
export const createNetworkCapabilities: <T extends NonEmptyArray<NetworkingAction>>(capabilities: T) => NetworkingCapabilities<T> = (capabilities) => {
    const entity = {};
    for (const capability of capabilities) {
        entity[capability] = {
            executing: false,
            error: null,
            abortController: null,
            abortControllers: [],
            ...!(capability === '_fetch') && { response: null }
        }
    }
    return entity as NetworkingCapabilities<typeof capabilities>;
}

export const createNetworkedList: <S, T extends NonEmptyArray<NetworkingAction>>(capabilities: NonEmptyArray<NetworkingAction>, data?: S[], filterParams?: FilterParams, count?: number, page?: number) => NetworkedList<S> & NetworkingCapabilities<T> = (capabilities, data = [], filterParams = {}, count, page = 1) => {
    return {
        data,
        ...createNetworkCapabilities(capabilities),
        filterParams,
        count,
        page
    };
}

export const execute: <T extends NetworkingAction>(action: T, abortControllers?: AbortController[]) => NetworkingCapability<T> = (action, abortControllers) => {
    return {
        [action]: {
            executing: true,
            error: null,
            ...!abortControllers && { abortController: new AbortController() },
            ...abortControllers && { abortControllers: abortControllers }
        }
    } as NetworkingCapability<typeof action>;
}

export const error: <T extends NetworkingAction>(action: T, error: string) => NetworkingCapability<T> = (action, error) => {
    return {
        [action]: {
            executing: false,
            error,
            abortController: null,
            abortControllers: []
        }
    } as NetworkingCapability<typeof action>;
}

export const success: <T extends NetworkingAction>(action: T, response: any, count?: number, page?: number, reducerMode?: StateReducerMode) => NetworkingCapability<T> = (action, response, count, page, reducerMode) => {
    const successState =  {
        executing: false,
        error: null,
        abortController: null,
        abortControllers: []
    };
    return (action === '_fetch') ?
        {
            ...Array.isArray(response) && { data: response } ,
            ...!Array.isArray(response) && {
                ...reducerMode && {_reducerMode: reducerMode},
                ...response
            },
            ...count && { count },
            ...page && { page },
            [action]: successState
        } :
        {
            [action]: {
                response:
                    {
                        ...reducerMode && { _reducerMode: reducerMode },
                        ...response
                    },
                ...successState
            }
        }
}

// Helpers
export const abort: <T>(action: NetworkingAction, entityOrList: T | NetworkedList<T>) => void = (action, entityOrList) => {
    if (entityOrList) {
        entityOrList[action]?.abortController?.abort();
        entityOrList[action]?.abortControllers?.forEach(abortController => abortController.abort());
    }
}
